import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  VesTransactionsService,
  VesTransactionItem,
  VesTransactionsPagination,
  VesTransactionsQueryParams,
} from '../../services/ves-transactions.service';
import { ApproveSwapModalComponent } from './approve-swap-modal/approve-swap-modal.component';
import { RejectSwapModalComponent } from './reject-swap-modal/reject-swap-modal.component';
import { DetailTransactionModalComponent } from './detail-transaction-modal/detail-transaction-modal.component';

@Component({
  selector: 'app-transactions-ves',
  templateUrl: './transactions-ves.component.html',
  styleUrls: ['./transactions-ves.component.sass'],
})
export class TransactionsVesComponent implements OnInit, OnDestroy {
  transactions: VesTransactionItem[] = [];
  pagination: VesTransactionsPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };

  filterForm: FormGroup;
  loading = false;
  errorMessage = '';
  expandedCardId: number | null = null;

  private destroy$ = new Subject<void>();

  pageSizeOptions = [10, 20, 50, 100];
  Math = Math;

  displayedColumns = [
    'userId',
    'userName',
    'userIdentity',
    'type',
    'debitVes',
    'creditVes',
    'balanceVes',
    'status',
    'createdAt',
    'actions',
  ];

  constructor(
    private vesTransactionsService: VesTransactionsService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    this.filterForm = this.fb.group({
      userId: [''],
      type: [''],
      status: [''],
      startDate: [''],
      endDate: [''],
    });
  }

  ngOnInit(): void {
    this.vesTransactionsService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    this.loadTransactions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  loadTransactions(): void {
    this.errorMessage = '';

    const params: VesTransactionsQueryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
      ...this.getFiltersFromForm(),
    };

    this.vesTransactionsService
      .getTransactions(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.transactions = response.data.items;
            this.pagination = response.data.pagination;
          } else {
            this.errorMessage = response.message || 'Error al cargar transacciones';
          }
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al cargar transacciones VES';
        },
      });
  }

  private getFiltersFromForm(): Partial<VesTransactionsQueryParams> {
    const filters: Partial<VesTransactionsQueryParams> = {};
    const formValue = this.filterForm.value;

    if (formValue.userId && formValue.userId.trim()) {
      filters.userId = formValue.userId.trim();
    }
    if (formValue.type && formValue.type.trim()) {
      filters.type = formValue.type.trim();
    }
    if (formValue.status && formValue.status.trim()) {
      filters.status = formValue.status.trim();
    }
    if (formValue.startDate) {
      const date = new Date(formValue.startDate);
      filters.startDate = date.toISOString().split('T')[0];
    }
    if (formValue.endDate) {
      const date = new Date(formValue.endDate);
      filters.endDate = date.toISOString().split('T')[0];
    }

    return filters;
  }

  applyFilters(): void {
    this.pagination.page = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.pagination.page = 1;
    this.loadTransactions();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pagination.limit = parseInt(target.value, 10);
    this.pagination.page = 1;
    this.loadTransactions();
  }

  onPaginatorChange(event: any): void {
    this.pagination.page = event.pageIndex + 1;
    this.pagination.limit = event.pageSize;
    this.loadTransactions();
  }

  formatVES(amount: number | null): string {
    if (amount === null) return '-';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-VE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Caracas',
    }).format(date);
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'completado':
        return '#16a34a';
      case 'pending':
      case 'pendiente':
        return '#ea580c';
      case 'rejected':
      case 'rechazado':
      case 'failed':
      case 'fallido':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }

  getTypeLabel(type: string): string {
    switch (type) {
      case 'SWAP_USDT_VES': return 'Swap USDT→VES';
      case 'CREDITO_MANUAL':
      case 'MANUAL_CREDIT': return 'Crédito Manual';
      case 'DEBITO_MANUAL':
      case 'MANUAL_DEBIT': return 'Débito Manual';
      case 'COMPRA_USDT': return 'Compra USDT';
      case 'COMPRA_USDT_VES': return 'Compra USDT (VES)';
      case 'RETIRO_USDT': return 'Retiro USDT';
      case 'RETIRO_USDT_VES': return 'Retiro USDT (VES)';
      case 'RETIRO_VES': return 'Retiro VES';
      case 'REVERSO_RETIRO_VES': return 'Reverso Retiro VES';
      case 'ENVIO_VES_BENEFICIARIO': return 'Envío VES Beneficiario';
      default: return type;
    }
  }

  isSwapBeneficiario(tx: VesTransactionItem): boolean {
    return tx?.type === 'RETIRO_VES' && !!tx?.referenceId?.startsWith('SWAP-USDT-VES-');
  }

  getDisplayType(tx: VesTransactionItem): string {
    if (this.isSwapBeneficiario(tx)) return 'Swap USDT→VES (beneficiario)';
    return this.getTypeLabel(tx.type);
  }

  getDisplayDebit(tx: VesTransactionItem): string {
    if (this.isSwapBeneficiario(tx)) return '—';
    return this.formatVES(tx.debitVes);
  }

  formatUSDT(amount: number | undefined): string {
    if (amount === undefined || amount === null) return '-';
    return `${amount.toFixed(2)} USDT`;
  }

  copyTransaction(tx: VesTransactionItem): void {
    const lines: string[] = [];

    lines.push(`Tipo: ${this.getTypeLabel(tx.type)}`);
    lines.push(`Estado: ${tx.status}`);
    lines.push(`Beneficiario: ${tx.userName}`);
    lines.push(`Email: ${tx.userEmail}`);
    lines.push(`Documento: ${tx.userIdType} ${tx.userIdNumber}`);

    if (tx.isCompany && tx.companyName) {
      lines.push(`Empresa: ${tx.companyName}${tx.companyRut ? ' · ' + tx.companyRut : ''}`);
    }

    if (tx.debitVes) {
      lines.push(`Débito: ${this.formatVES(tx.debitVes)}`);
    }
    if (tx.creditVes) {
      lines.push(`Crédito: ${this.formatVES(tx.creditVes)}`);
    }
    lines.push(`Saldo: ${this.formatVES(tx.balanceVes)}`);

    if (tx.usdtAmount) {
      lines.push(`Monto USDT: ${this.formatUSDT(tx.usdtAmount)}`);
    }
    if (tx.vesRate) {
      lines.push(`Tasa: ${tx.vesRate}`);
    }

    if (tx.banco) {
      lines.push(`Banco: ${tx.banco}`);
    }
    if (tx.operador) {
      lines.push(`Operador: ${tx.operador}`);
    }
    if (tx.operatorEmail) {
      lines.push(`Email Operador: ${tx.operatorEmail}`);
    }
    if (tx.referenceId) {
      lines.push(`Referencia: ${tx.referenceId}`);
    }
    if (tx.description) {
      lines.push(`Descripción: ${tx.description}`);
    }

    if (tx.walletAddress) {
      lines.push(`Red: ${tx.networkBinance}`);
      lines.push(`Wallet: ${tx.walletAddress}`);
      if (tx.withdrawalFee) {
        lines.push(`Fee: ${tx.withdrawalFee} USDT`);
      }
    }

    lines.push(`Fecha: ${this.formatDate(tx.createdAt)}`);

    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.copiedTxId = tx.id;
      setTimeout(() => this.copiedTxId = null, 2000);
    });
  }

  copiedTxId: string | null = null;

  viewDetail(tx: VesTransactionItem): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(DetailTransactionModalComponent, {
      data: tx,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
  }

  approveTransaction(tx: VesTransactionItem): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(ApproveSwapModalComponent, {
      data: tx,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  rejectTransaction(tx: VesTransactionItem): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RejectSwapModalComponent, {
      data: tx,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }
}
